import { useCallback, useEffect, useRef, useState } from "react";
import { toApiError } from "../../../lib/http/errors";
import { Button, ButtonLink } from "../../../components/ui/Button";
import { ErrorBanner, Loading } from "../../../components/Shell";
import type { TopBarSpec } from "../../../components/TopBar";
import { useI18n } from "../../../i18n";
import { ROOM_REPLAY_PLAYER } from "../../../constants/flags";
import { ShareLinks, useShareCtx } from "../../share";
import type { ShareCtx } from "../../share";
import { SegGroup } from "../../../components/ui/Seg";
import { VideoExpired, VideoNoticeFailed } from "../api/videoApi";
import type { VideoApi } from "../api/videoApi";
import type { VideoJob, VideoResolution } from "../types";
import {
  VIDEO_STEPS,
  canDownload,
  canRetry,
  counts,
  fmtDate,
  fmtDuration,
  fmtSize,
  isExpiredJob,
  isPending,
  sortJobs,
} from "../lib/logic";
import "../../../styles/video.css";

/** 진행 중인 작업이 있으면 자주, 없으면 가끔 다시 읽는다(렌더는 룸이 닫힌 뒤에도 끝난다) */
export const VIDEO_POLL_MS = 3000;
export const VIDEO_IDLE_POLL_MS = 15_000;

/** 서명 URL 로 내려받기 — 새 창 없이 <a download> (서명 URL 은 짧게 살아서 목록에 남기지 않는다) */
export function saveFromUrl(url: string, fileName: string | null) {
  const a = document.createElement("a");
  a.href = url;
  if (fileName) a.download = fileName;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

type Save = (url: string, fileName: string | null) => void;

/** 새 탭 열기(시험이 바꿔 끼운다) — 기본 window.open */
export type OpenTab = (url: string, target: string) => Window | null;
const openTab: OpenTab = (url, target) => window.open(url, target);

/** 목록 읽기 + 진행 중이면 3초마다 다시 — 겹쳐 부르지 않는다 */
function useVideos(api: VideoApi, filter: { roomId?: string | null; replayId?: string | null }) {
  const [jobs, setJobs] = useState<VideoJob[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef(false);
  const { roomId = null, replayId = null } = filter;
  const load = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      setJobs(await api.list({ roomId, replayId }));
      setError(null);
    } catch (e) {
      setError(toApiError(e).code);
    } finally {
      inflight.current = false;
    }
  }, [api, roomId, replayId]);
  const pending = (jobs ?? []).some((j) => isPending(j.status));
  useEffect(() => {
    void load();
    const h = window.setInterval(() => void load(), pending ? VIDEO_POLL_MS : VIDEO_IDLE_POLL_MS);
    return () => window.clearInterval(h);
  }, [load, pending]);
  return { jobs, error, load, setJobs };
}

/** 작업 한 줄을 바꿔 끼운다(없으면 앞에 붙인다) */
const upsert = (xs: VideoJob[] | null, j: VideoJob | null) =>
  !j
    ? xs
    : xs?.some((x) => x.jobId === j.jobId)
      ? xs.map((x) => (x.jobId === j.jobId ? j : x))
      : [j, ...(xs ?? [])];

function useActions(
  api: VideoApi,
  setJobs: (f: (xs: VideoJob[] | null) => VideoJob[] | null) => void,
  load: () => Promise<void>,
  onError: (e: unknown) => void,
  onDone: (text: string) => void,
  save: Save,
  open: OpenTab = openTab,
) {
  const { t } = useI18n();
  const [busy, setBusy] = useState<string | null>(null);
  /** 새 탭이 막혔을 때 카드에 보일 공지 초안 링크(jobId → editUrl) */
  const [noticeLinks, setNoticeLinks] = useState<Readonly<Record<string, string>>>({});
  /** FE-504 로 거절된 작업 · 리플레이(만료 표시로 바꾼다) */
  const [expired, setExpired] = useState<ReadonlySet<string>>(new Set());
  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    try {
      await fn();
    } catch (e) {
      if (e instanceof VideoExpired) setExpired((s) => new Set(s).add(key.split(":")[1] ?? key));
      else if (e instanceof VideoNoticeFailed)
        onDone(
          t(
            e.reason === "tooLarge"
              ? "video.notice.tooLarge"
              : e.reason === "setup"
                ? "video.notice.setup"
                : "video.notice.failed",
          ),
        );
      else onError(e);
    } finally {
      setBusy(null);
    }
  };
  return {
    busy,
    expired,
    noticeLinks,
    /**
     * '공지로 보내기'(SR-REC-04 · CRQ-003 D-1) — 클릭 순간 빈 탭을 먼저 연다(팝업 차단 방지). 초안이 오면 opener 를 끊고 Campus 편집 화면으로
     * 보낸다(Campus 는 Field 창을 알 필요가 없다). 413 이면 720p 작업이 목록에 생기고 '다 되면 그 영상으로 다시 보내 주세요'.
     */
    notice: (j: VideoJob) => {
      const tab = open("", "_blank");
      return run(`notice:${j.jobId}`, async () => {
        try {
          const r = await api.notice(j.jobId);
          if (r.kind === "rerender") {
            tab?.close();
            setJobs((xs) => upsert(xs, r.job));
            onDone(t("video.notice.rerender"));
            await load();
            return;
          }
          if (tab && !tab.closed) {
            tab.opener = null;
            tab.location.href = r.editUrl;
          } else setNoticeLinks((m) => ({ ...m, [j.jobId]: r.editUrl }));
          onDone(t("video.notice.sent"));
          await load();
        } catch (e) {
          tab?.close();
          throw e;
        }
      });
    },
    retry: (j: VideoJob) =>
      run(`retry:${j.jobId}`, async () => {
        const r = await api.retry(j.jobId);
        setJobs((xs) => upsert(xs, r));
        onDone(t("video.retried"));
        await load();
      }),
    download: (j: VideoJob) =>
      run(`download:${j.jobId}`, async () => {
        const d = await api.download(j.jobId);
        save(d.url, d.fileName);
        onDone(t("video.downloaded"));
      }),
    request: (replayId: string, resolution: VideoResolution) =>
      run(`request:${replayId}`, async () => {
        const r = await api.request({ replayId, resolution });
        setJobs((xs) => upsert(xs, r));
        onDone(t("video.requested"));
        await load();
      }),
  };
}

/**
 * 영상 한 개(FS-14-02 · SCR v0.7 검토 4-1~4-3 · 4-5 · 4-9 · 4-10):
 *  제목 줄(출처 칩 · 제목) · 상태 알약 5종(대기 n번째 · 렌더 중 % · 다시 시도 중 n/3 · 만들지 못했어요 · 완료)
 *  · '1080p30 MP4 · 별명만 표시' · '성공 구간만 이어 붙임'(cut stages) · 완료면 길이·크기·해상도 + ⬇ 다운로드
 *  · 진행 중이면 막대 + 긴 영상 안내 · 재시도 중은 '선생님이 할 일 없음' · 만들지 못했어요는 FE-502 + ↻ 다시 요청
 *  · 만료(FE-504)면 다운로드·요청 없이 '오래된 기록이라 영상을 만들 수 없어요' · 만료일 안내(MP4 도 같은 날 지워진다).
 *  워커의 해시 불일치(FE-501)도 교사에게는 FE-502 로만 보인다(4-9).
 */
export function VideoCard({
  job,
  busy,
  expiredNow,
  onRetry,
  onDownload,
  onNotice,
  noticeLink = null,
  replayLink = true,
  share,
}: {
  job: VideoJob;
  busy: string | null;
  expiredNow: boolean;
  onRetry: () => void;
  onDownload: () => void;
  /** '공지로 보내기'(완료 영상만 · 없으면 버튼 없음) */
  onNotice?: () => void;
  /** 새 탭이 막혔을 때 보일 공지 초안 링크 */
  noticeLink?: string | null;
  replayLink?: boolean;
  /** P3-S5 보호자 공유 링크(플래그 ON 때만 — OFF 면 undefined 라 영역이 없다 · 4-7/4-8) */
  share?: { ctx: ShareCtx; onError: (e: unknown) => void; onDone: (text: string) => void };
}) {
  const { t, locale } = useI18n();
  const expired = expiredNow || isExpiredJob(job);
  const on = job.status;
  const pill = (s: (typeof VIDEO_STEPS)[number]) => {
    if (s !== on) return t(s === "retrying" ? "video.step.retryingPlain" : `video.step.${s}`);
    if (s === "queued" && job.queuePos) return t("video.step.queuedPos", { n: job.queuePos });
    if (s === "rendering" && job.progress !== null)
      return t("video.step.renderingPct", { n: job.progress });
    return s === "retrying"
      ? t("video.step.retrying", {
          n: Math.min(Math.max(job.attempts, 1), job.maxAttempts),
          max: job.maxAttempts,
        })
      : t(`video.step.${s}`);
  };
  const expiresOn = fmtDate(job.expiresAt, locale);
  const title = job.title || t(`video.source.${job.source}`);
  // 공유 대상 — 영상은 완료 작업 · 리플레이는 관찰자 재생 경로가 있는 릴레이 완성만
  const shareReplay =
    ROOM_REPLAY_PLAYER && job.source === "relay" && job.replayId ? job.replayId : null;
  const shareJob = on === "done" ? job.jobId : null;
  return (
    <li
      className={`pf-video__item pf-video__item--${on}${expired ? " pf-video__item--expired" : ""}`}
      data-testid="video-item"
      data-job={job.jobId}
      data-status={on}
      data-source={job.source}
    >
      <div className="pf-video__head">
        <span
          className={`pf-chip-static ${job.source === "relay" ? "pf-chip-static--info" : job.source === "carry" ? "pf-chip-static--ok" : ""}`}
          data-testid="video-source"
        >
          <span className="pf-sr-only">{t("video.sourceLabel")}: </span>
          {t(`video.source.${job.source}`)}
        </span>
        <b className="pf-video__title">{title}</b>
        {job.createdAt && (
          <span className="pf-muted pf-row__end">
            {t("video.created", { date: fmtDate(job.createdAt, locale) ?? "" })}
          </span>
        )}
      </div>
      <ul className="pf-state" aria-label={t("video.stateLabel")} data-testid="video-steps">
        {VIDEO_STEPS.map((s) => (
          <li
            key={s}
            className={`pf-state__pill${s === on ? " pf-state__pill--on" : ""}${s === on && s === "dead" ? " pf-video__pill--dead" : ""}`}
            aria-current={s === on ? "true" : undefined}
            data-step={s}
          >
            {pill(s)}
          </li>
        ))}
      </ul>
      <p className="pf-muted" data-testid="video-spec">
        {t("video.spec", { res: job.resolution })}
        {" · "}
        <span className="pf-video__cut" data-cut={job.cut} data-testid="video-cut">
          {t(`video.cut.${job.cut}`)}
        </span>
        {job.status === "done" && job.durationS !== null && job.sizeBytes !== null && (
          <>
            {" · "}
            <b data-testid="video-meta">
              {t("video.doneMeta", {
                len: fmtDuration(job.durationS),
                size: fmtSize(job.sizeBytes),
                res: job.resolution,
              })}
            </b>
          </>
        )}
      </p>
      {job.cut === "stages" && on !== "done" && !expired && (
        <p className="pf-muted pf-video__note">{t("video.cutStagesHint")}</p>
      )}
      {on === "rendering" && job.progress !== null && (
        <div
          className="pf-progress"
          role="progressbar"
          aria-label={t("video.progressLabel")}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={job.progress}
          data-testid="video-progress"
        >
          <i className="pf-progress__fill" style={{ width: `${job.progress}%` }} />
        </div>
      )}
      {on === "queued" && job.queuePos !== null && job.queuePos > 1 && (
        <p className="pf-muted">{t("video.queuedNote", { n: job.queuePos - 1 })}</p>
      )}
      {on === "retrying" && (
        <p className="pf-muted" data-testid="video-retrying">
          {t("video.retryingNote")}
        </p>
      )}
      {isPending(on) && !expired && (
        <p className="pf-video__note" data-testid="video-long">
          <span aria-hidden="true">⏳ </span>
          {t("video.longNote")}
        </p>
      )}
      {expired ? (
        <div
          className="pf-banner pf-banner--info"
          role="status"
          data-testid="video-expired"
          data-fe-code="FE-504"
        >
          <div className="pf-banner__text">
            <b>{t("video.expiredCause")}</b> {t("video.expiredNext")}{" "}
            <span className="pf-banner__code">({t("common.codeLabel", { code: "FE-504" })})</span>
          </div>
        </div>
      ) : (
        on === "dead" && (
          <div
            className="pf-banner pf-banner--warn"
            role="status"
            data-testid="video-dead"
            data-fe-code="FE-502"
          >
            <div className="pf-banner__text">
              <b>{t("video.deadCause")}</b> {t("video.deadNext")}{" "}
              <span className="pf-banner__code">({t("common.codeLabel", { code: "FE-502" })})</span>
            </div>
            <div className="pf-banner__actions">
              <Button
                variant="ghost"
                icon="↻"
                className="pf-btn--sm"
                disabled={busy !== null || !canRetry(job)}
                aria-label={t("video.retryOf", { title })}
                data-testid="video-retry"
                onClick={onRetry}
              >
                {t("video.retry")}
              </Button>
            </div>
          </div>
        )
      )}
      <div className="pf-row pf-row--wrap">
        {!expired && (
          <Button
            variant={on === "done" ? "green" : "ghost"}
            icon="⬇"
            className="pf-btn--sm"
            disabled={busy !== null || !canDownload(job)}
            aria-label={t("video.downloadOf", { title })}
            data-testid="video-download"
            onClick={onDownload}
          >
            {t("video.download")}
          </Button>
        )}
        {onNotice && !expired && on === "done" && (
          <Button
            variant="ghost"
            icon="📌"
            className="pf-btn--sm"
            disabled={busy !== null}
            aria-label={t("video.notice.sendOf", { title })}
            aria-busy={busy === `notice:${job.jobId}` ? "true" : undefined}
            data-testid="video-notice"
            data-attached={job.notice ? "true" : undefined}
            onClick={onNotice}
          >
            {busy === `notice:${job.jobId}`
              ? t("video.notice.sending")
              : job.notice
                ? t("video.notice.open")
                : t("video.notice.send")}
          </Button>
        )}
        {job.notice && !expired && (
          <span className="pf-chip-static pf-chip-static--ok" data-testid="video-notice-attached">
            {t("video.notice.attached")}
          </span>
        )}
        {/* 다시 보기는 릴레이 완성 리플레이만(제어 평면이 반 구성원에게 여는 권위 리플레이 — 협동 운반 조각은 아직 뷰어 경로 없음) */}
        {replayLink && ROOM_REPLAY_PLAYER && job.source === "relay" && job.replayId && !expired && (
          <ButtonLink
            to={`/replay/${encodeURIComponent(job.replayId)}`}
            variant="ghost"
            icon="▶"
            className="pf-btn--sm"
            data-testid="video-replay"
          >
            {t("video.replay")}
          </ButtonLink>
        )}
        {expiresOn && !expired && (
          <span className="pf-muted pf-row__end" data-testid="video-expires">
            {t("video.expires", { date: expiresOn })}
          </span>
        )}
      </div>
      {noticeLink && (
        <p className="pf-muted" role="status" data-testid="video-notice-link">
          {t("video.notice.blocked")}{" "}
          <a href={noticeLink} target="_blank" rel="noopener noreferrer">
            {t("video.notice.open")}
          </a>
        </p>
      )}
      {share && !expired && (shareReplay || shareJob) && (
        <ShareLinks
          ctx={share.ctx}
          replayId={shareReplay}
          jobId={shareJob}
          onError={share.onError}
          onDone={share.onDone}
        />
      )}
    </li>
  );
}

/** Campus 공지 안내(4-6) — 2026-09-28 CRQ-003 D-1 회신으로 카드마다 '공지로 보내기'. 공유 링크 영역은 플래그 OFF 면 통째로 없음(4-8 · P3-S5) */
function CampusHint() {
  const { t } = useI18n();
  return (
    <div className="pf-banner pf-banner--info" role="note" data-testid="video-campus-hint">
      <div className="pf-banner__text">
        <span aria-hidden="true">📎 </span>
        {t("video.campusHint")}
      </div>
    </div>
  );
}

/**
 * FS-14-02 콘솔 탭(/teacher/room/:roomId/video · SCR v0.7 검토 4-4 · v07-16~19) — 룸이 닫혀도 열린다(assess 탭과 같음).
 * 그 룸에서 나온 영상 목록(릴레이 완성 · 협동 운반 팀마다 하나 — 목록형). roomId 가 없으면 코스 전체.
 */
export function VideoTab({
  api,
  roomId,
  courseLabel,
  onTop,
  onError,
  onDone,
  save = saveFromUrl,
  open = openTab,
}: {
  api: VideoApi;
  roomId: string | null;
  courseLabel: string;
  onTop: (spec: TopBarSpec) => void;
  onError: (e: unknown) => void;
  onDone: (text: string) => void;
  save?: Save;
  open?: OpenTab;
}) {
  const { t } = useI18n();
  const { jobs, error, load, setJobs } = useVideos(api, { roomId });
  const act = useActions(api, setJobs, load, onError, onDone, save, open);
  const shareCtx = useShareCtx();
  const share = shareCtx ? { ctx: shareCtx, onError, onDone } : undefined;
  const c = counts(jobs ?? []);
  const top = JSON.stringify([courseLabel, c]);
  useEffect(() => {
    const chips = [
      { label: t("video.chipDone", { n: c.done }), testId: "video-chip-done" },
      ...(c.pending
        ? [{ label: t("video.chipPending", { n: c.pending }), testId: "video-chip-pending" }]
        : []),
      ...(c.dead ? [{ label: t("video.chipDead", { n: c.dead }), testId: "video-chip-dead" }] : []),
    ];
    onTop({ icon: "🎬", title: t("video.topTitle", { course: courseLabel }), chips });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [top]);

  return (
    <section
      className="pf-pane pf-pane--hero pf-video"
      aria-labelledby="video-title"
      data-testid="video-panel"
    >
      <h2 id="video-title" className="pf-h3">
        {t("video.listTitle")}
      </h2>
      <p className="pf-muted">{t("video.listHint")}</p>
      {error && (
        <ErrorBanner
          cause={t("video.loadFailed")}
          action={t("teacher.common.retryLater")}
          code={error}
        />
      )}
      {!jobs ? (
        !error && <Loading />
      ) : jobs.length === 0 ? (
        <p className="pf-muted" data-testid="video-empty">
          {t("video.empty")}
        </p>
      ) : (
        <ul className="pf-video__list" data-testid="video-list">
          {sortJobs(jobs).map((j) => (
            <VideoCard
              key={j.jobId}
              job={j}
              busy={act.busy}
              expiredNow={act.expired.has(j.jobId) || act.expired.has(j.replayId)}
              onRetry={() => void act.retry(j)}
              onDownload={() => void act.download(j)}
              onNotice={() => void act.notice(j)}
              noticeLink={act.noticeLinks[j.jobId] ?? null}
              share={share}
            />
          ))}
        </ul>
      )}
      <p className="pf-muted" data-testid="video-download-hint">
        {t("video.downloadHint")}
      </p>
      <CampusHint />
    </section>
  );
}

/**
 * /replay/:id 교사 뷰 오른쪽 페인(와이어 sfs-14 'FS-14-02 영상 · 공유 (교사)' · 4-4 '교사 뷰 패널은 유지').
 * 이 리플레이로 만든 영상 + 없으면 '영상 만들기'(해상도 1080 기본 · 720 선택). 공유 링크 영역은 숨김(4-8).
 */
export function ReplayVideoPanel({
  api,
  replayId,
  onError,
  onDone,
  save = saveFromUrl,
  open = openTab,
}: {
  api: VideoApi;
  replayId: string;
  onError: (e: unknown) => void;
  onDone: (text: string) => void;
  save?: Save;
  open?: OpenTab;
}) {
  const { t } = useI18n();
  const { jobs, error, load, setJobs } = useVideos(api, { replayId });
  const act = useActions(api, setJobs, load, onError, onDone, save, open);
  const [res, setRes] = useState<VideoResolution>(1080);
  const shareCtx = useShareCtx();
  const share = shareCtx ? { ctx: shareCtx, onError, onDone } : undefined;
  const expired = act.expired.has(replayId) || (jobs ?? []).some((j) => isExpiredJob(j));
  return (
    <aside
      className="pf-pane pf-pane--hero pf-replay__side pf-video"
      aria-labelledby="rv-title"
      data-testid="replay-video"
    >
      <h2 id="rv-title" className="pf-h2">
        {t("video.paneTitle")}
      </h2>
      {error && (
        <ErrorBanner
          cause={t("video.loadFailed")}
          action={t("teacher.common.retryLater")}
          code={error}
        />
      )}
      {!jobs ? (
        !error && <Loading />
      ) : (
        <>
          {jobs.length > 0 && (
            <ul className="pf-video__list">
              {sortJobs(jobs).map((j) => (
                <VideoCard
                  key={j.jobId}
                  job={j}
                  busy={act.busy}
                  replayLink={false}
                  expiredNow={act.expired.has(j.jobId) || act.expired.has(replayId)}
                  onRetry={() => void act.retry(j)}
                  onDownload={() => void act.download(j)}
                  onNotice={() => void act.notice(j)}
                  noticeLink={act.noticeLinks[j.jobId] ?? null}
                  share={share}
                />
              ))}
            </ul>
          )}
          {jobs.length === 0 &&
            (expired ? (
              <div
                className="pf-banner pf-banner--info"
                role="status"
                data-testid="video-expired"
                data-fe-code="FE-504"
              >
                <div className="pf-banner__text">
                  <b>{t("video.expiredCause")}</b> {t("video.expiredNext")}{" "}
                  <span className="pf-banner__code">
                    ({t("common.codeLabel", { code: "FE-504" })})
                  </span>
                </div>
              </div>
            ) : (
              <>
                <p className="pf-muted" data-testid="video-none">
                  {t("video.noneForReplay")}
                </p>
                <SegGroup label={t("video.resolution")} variant="pill" testId="video-res">
                  {([1080, 720] as const).map((r) => (
                    <label key={r} className="pf-seg__opt">
                      <input
                        type="radio"
                        name="pf-video-res"
                        checked={res === r}
                        onChange={() => setRes(r)}
                      />
                      <span>{t(`video.res.${r}`)}</span>
                    </label>
                  ))}
                </SegGroup>
                <Button
                  variant="primary"
                  icon="🎬"
                  disabled={act.busy !== null}
                  aria-label={t("video.requestOf")}
                  data-testid="video-request"
                  onClick={() => void act.request(replayId, res)}
                >
                  {t("video.request")}
                </Button>
                {/* 영상이 아직 없어도 리플레이 링크는 만들 수 있다(P3-S5 · 플래그 ON) */}
                {share && ROOM_REPLAY_PLAYER && (
                  <ShareLinks
                    ctx={share.ctx}
                    replayId={replayId}
                    jobId={null}
                    onError={onError}
                    onDone={onDone}
                  />
                )}
              </>
            ))}
        </>
      )}
      <CampusHint />
    </aside>
  );
}
